<template>
	<div ref="container">
		<div class="project-profit-table__title-container">
			<h3 class="project-profit-table__heading">
				{{ $t('profit') }}
			</h3>
			<SuiInfoButton as="a" :href="PROFIT_TABLE_HELP_ARTICLE_URL">
				{{ $t('project_learn_more') }}
			</SuiInfoButton>
		</div>
		<div class="project-profit-table">
			<ProjectProfitHeadings
				:loading="loading"
				:can-see-margin-and-markup="canSeeMarginAndMarkup"
				:can-see-cost-breakdown="canSeeCostBreakdown"
				:can-see-profit="canSeeProfit"
			/>
			<div class="project-profit-table">
				<div class="project-profit-table__data">
					<SuiDataTable
						:col-definition="colDefinitions"
						:loading="loading"
						:loading-label="$t('spa__general.loading')"
					>
						<ProjectProfitTableRevenue
							:revenue-budgeted="revenueBudgeted"
							:revenue-earned="revenueEarned"
							:revenue-recognized="revenueRecognized"
							:revenue-invoiced="revenueInvoiced"
							:revenue-forecasted="revenueForecasted"
							:is-budget-set="isRevenueBudgetSet"
							:is-any-budget-set="isAnyBudgetSet"
							:can-see-recognized="canSeeRecognized"
							:can-see-forecast="canSeeForecast"
							:is-revenue-tm-model="isRevenueTmModel"
						/>
						<ProjectProfitTableCost
							v-if="canSeeMarginAndMarkup"
							row-class="project-profit-table-cost__regular-row"
							:cost-budgeted="costBudgeted"
							:cost-recognized="costRecognized"
							:cost-invoiced="costInvoiced"
							:cost-forecasted="costForecasted"
							:is-cost-budget-set="isCostBudgetSet"
							:is-any-budget-set="isAnyBudgetSet"
							:can-see-recognized="canSeeRecognized"
							:can-see-forecast="canSeeForecast"
							:is-revenue-tm-model="isRevenueTmModel"
						/>
						<ProjectProfitTableCost
							v-if="canSeeCostBreakdown"
							test-id="labor-cost-row"
							row-class="project-profit-table-cost__narrow-row project-profit-table-cost__shadow"
							:cost-budgeted="internalCostBudgeted"
							:cost-recognized="internalCostRecognized"
							:cost-invoiced="internalCostInvoiced"
							:cost-forecasted="internalCostForecasted"
							:is-cost-budget-set="isInternalCostBudgetSet"
							:is-any-budget-set="isAnyBudgetSet"
							:can-see-recognized="canSeeRecognized"
							:can-see-forecast="canSeeForecast"
							:is-revenue-tm-model="isRevenueTmModel"
						/>
						<ProjectProfitTableCost
							v-if="canSeeCostBreakdown"
							test-id="external-cost-row"
							row-class="project-profit-table-cost__narrow-row"
							:cost-budgeted="externalCostBudgeted"
							:cost-recognized="externalCostRecognized"
							:cost-invoiced="externalCostInvoiced"
							:cost-forecasted="externalCostForecasted"
							:is-cost-budget-set="isExternalCostBudgetSet"
							:is-any-budget-set="isAnyBudgetSet"
							:can-see-recognized="canSeeRecognized"
							:can-see-forecast="canSeeForecast"
							:is-revenue-tm-model="isRevenueTmModel"
						/>
						<ProjectProfitTableTotalsRow
							v-if="canSeeProfit"
							:is-any-budget-set="isAnyBudgetSet"
							:cost-budgeted="costBudgeted"
							:cost-recognized="costRecognized"
							:cost-invoiced="costInvoiced"
							:cost-forecasted="costForecasted"
							:revenue-budgeted="revenueBudgeted"
							:revenue-earned="revenueEarned"
							:revenue-recognized="revenueRecognized"
							:revenue-invoiced="revenueInvoiced"
							:revenue-forecasted="revenueForecasted"
							:can-see-recognized="canSeeRecognized"
							:can-see-forecast="canSeeForecast"
							:is-revenue-tm-model="isRevenueTmModel"
							:is-revenue-tm-not-capped="isRevenueTmNotCapped"
						/>
					</SuiDataTable>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import ProjectProfitHeadings from './ProjectProfitHeadings.vue';
import {
	SuiDataTable,
	SuiDataTableColumnAlign,
	type SuiDataTableColumnType,
	SuiInfoButton,
} from '@developers/scoro-ui';
import { computed, reactive, toRefs, watch } from 'vue';
import { Setting } from '@/constants/settings';
import { usePermissions, useTranslation } from '@/composables';
import { ProjectProfitTableColumnTypes } from './types';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { Permission, PermissionAction } from '@/constants/permissions';
import ProjectProfitTableCost from './ProjectProfitTableCost.vue';
import ProjectProfitTableTotalsRow from './ProjectProfitTableTotalsRow.vue';
import ProjectProfitTableRevenue from './ProjectProfitTableRevenue.vue';
import { PROFIT_TABLE_HELP_ARTICLE_URL } from './constants';
import {
	type RetainerPeriod,
	type Project,
	ProjectRevenueCalcModel,
	useProjectProfitTableQuery,
	type ProjectProfitTableQueryVariables,
} from '@/generated/graphql';
import {
	APOLLO_PARAMS_PERSISTED_QUERY,
	usePersistedQueryLoading,
	useRefetchOnSocketMessage,
} from '@/lib/apollo/apolloTools';
import { SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING } from '../../constants';
import { Feature } from '@/constants/features';

export type ProjectProfitTableProps = {
	projectId: Project['id'];
	retainerPeriodId?: RetainerPeriod['id'];
};

const props = defineProps<ProjectProfitTableProps>();

const { projectId, retainerPeriodId } = toRefs(props);

const { t } = useTranslation();
const { can, userHas } = usePermissions();

const variables = reactive<ProjectProfitTableQueryVariables>({
	projectInput: {
		projectId: Number(projectId.value),
	},
	projectProfitDataInput: {
		retainerPeriodId: retainerPeriodId.value,
	},
});

const { loading, result } = useRefetchOnSocketMessage(
	usePersistedQueryLoading(
		useProjectProfitTableQuery(variables, APOLLO_PARAMS_PERSISTED_QUERY)
	),
	{
		messages: SOCKET_MESSAGES_THAT_TRIGGER_REFETCHING,
		filter: (message) =>
			String(message.data.project_id) === projectId.value,
	}
);

watch(retainerPeriodId, (retainerPeriodId) => {
	variables.projectProfitDataInput.retainerPeriodId = retainerPeriodId;
});

const profitData = computed(() => result.value?.Project?.profitData);

const isRevenueTmModel = computed(() => {
	const model = result.value?.Project?.revenueCalcModel;
	return (
		model === ProjectRevenueCalcModel.TmCapped ||
		model === ProjectRevenueCalcModel.TmNotCapped
	);
});

const isRevenueTmNotCapped = computed(
	() =>
		result.value?.Project?.revenueCalcModel ===
		ProjectRevenueCalcModel.TmNotCapped
);

const isRevenueBudgetSet = computed(() => !!profitData?.value?.revenueBudget);

const isInternalCostBudgetSet = computed(
	() => !!profitData?.value?.internalCostBudget
);

const isExternalCostBudgetSet = computed(
	() => !!profitData?.value?.externalCostBudget
);

const isCostBudgetSet = computed(() => !!profitData?.value?.costBudget);

const isAnyBudgetSet = computed(() => {
	return (
		isRevenueBudgetSet.value ||
		isInternalCostBudgetSet.value ||
		isExternalCostBudgetSet.value ||
		isCostBudgetSet.value
	);
});

const revenueBudgeted = computed(() => profitData.value?.revenueBudget ?? 0);
const revenueRecognized = computed(
	() => profitData.value?.revenueRecognized ?? 0
);
const revenueInvoiced = computed(() => profitData.value?.revenueInvoiced ?? 0);
const revenueForecasted = computed(
	() => profitData.value?.revenueForecasted ?? 0
);
const revenueEarned = computed(() => profitData.value?.revenueEarned ?? 0);

const costBudgeted = computed(() => profitData.value?.costBudget ?? 0);
const costRecognized = computed(() => profitData.value?.costRecognized ?? 0);
const costInvoiced = computed(() => profitData.value?.costInvoiced ?? 0);
const costForecasted = computed(() => profitData.value?.costForecasted ?? 0);

const internalCostBudgeted = computed(
	() => profitData.value?.internalCostBudget ?? 0
);
const internalCostRecognized = computed(
	() => profitData.value?.internalCostRecognized ?? 0
);
const internalCostInvoiced = computed(
	() => profitData.value?.internalCostInvoiced ?? 0
);
const internalCostForecasted = computed(
	() => profitData.value?.internalCostForecasted ?? 0
);

const externalCostBudgeted = computed(
	() => profitData.value?.externalCostBudget ?? 0
);
const externalCostRecognized = computed(
	() => profitData.value?.externalCostRecognized ?? 0
);
const externalCostInvoiced = computed(
	() => profitData.value?.externalCostInvoiced ?? 0
);
const externalCostForecasted = computed(
	() => profitData.value?.externalCostForecasted ?? 0
);

const [showLaborCost] = useBooleanSetting(Setting.SHOW_EVENT_PRICE);

const canSeeOtherLaborCost = computed(() => {
	return can(
		PermissionAction.HAS_PERMISSION,
		Permission.CAN_SEE_OTHER_LABORCOST
	);
});

const canSeeMarginAndMarkup = computed(() =>
	can(PermissionAction.HAS_PERMISSION, Permission.CAN_SEE_MARGIN_AND_MARKUP)
);

const canSeeCostBreakdown = computed(() => {
	return (
		canSeeMarginAndMarkup.value &&
		canSeeOtherLaborCost.value &&
		showLaborCost.value
	);
});

// if labor cost setting is on, while not having permission to see other labor cost, then profit cannot be shown.
const canSeeProfit = computed(() => {
	return (
		(canSeeOtherLaborCost.value ||
			(!showLaborCost.value && !canSeeOtherLaborCost.value)) &&
		canSeeMarginAndMarkup.value
	);
});

const canSeeRecognized = computed(() => {
	return userHas(Feature.REVENUE_FORECAST);
});

const canSeeForecast = computed(() => {
	return userHas(Feature.PROJECT_CHARTS);
});

const colDefinitions = computed<SuiDataTableColumnType[]>(() => {
	return <SuiDataTableColumnType[]>[
		{
			id: ProjectProfitTableColumnTypes.BUDGETED,
			title: t('budgeted'),
			align: SuiDataTableColumnAlign.RIGHT,
			width: '14%',
		},
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.EARNED,
						title: t('earned'),
						align: SuiDataTableColumnAlign.RIGHT,
						iconName: 'icon-info-16',
						iconLabel: t('earned_revenue_tooltip'),
						width: '10%',
					},
					{
						id: ProjectProfitTableColumnTypes.RECOGNIZED_TO_DATE,
						title: t('recognized_to_date'),
						align: SuiDataTableColumnAlign.RIGHT,
						width: '16%',
					},
				]
			: []),
		...(canSeeRecognized.value &&
		!isRevenueTmModel.value &&
		(isRevenueBudgetSet.value || isCostBudgetSet.value)
			? [
					{
						id: ProjectProfitTableColumnTypes.TO_RECOGNIZE,
						title: t('to_recognize'),
						align: SuiDataTableColumnAlign.LEFT,
						iconName: 'icon-info-16',
						iconLabel: t('to_recognize_tooltip'),
						width: '14%',
					},
				]
			: []),
		{
			id: ProjectProfitTableColumnTypes.INVOICED_TO_DATE,
			title: t('invoiced_to_date'),
			align: SuiDataTableColumnAlign.RIGHT,
			width: '16%',
		},
		{
			id: ProjectProfitTableColumnTypes.TO_INVOICE,
			title: t('to_invoice'),
			align: SuiDataTableColumnAlign.LEFT,
			iconName: 'icon-info-16',
			iconLabel: isRevenueTmModel.value
				? t('to_invoice_tm_tooltip')
				: t('to_invoice_tooltip'),
			width: '15%',
		},
		...(canSeeForecast.value
			? [
					{
						id: ProjectProfitTableColumnTypes.FORECASTED,
						title: t('forecasted'),
						align: SuiDataTableColumnAlign.RIGHT,
						width: '15%',
					},
				]
			: []),
	];
});
</script>

<style scoped lang="scss">
.project-profit-table {
	display: flex;
	width: 100%;
	overflow: hidden;

	&__title-container {
		display: flex;
		gap: 2rem;
		align-items: center;
	}

	&__heading {
		padding: 1.125rem 0 1rem;
	}

	&__data {
		width: 100%;
		text-wrap: nowrap;

		:deep(.sui-data-table-header__col) {
			padding: 3.25rem 1.5rem 2.25rem;
		}
	}
}
</style>
